/**
 * Cart Drawer Component
 * Handles opening/closing cart drawer and cart functionality
 * Completely event-driven architecture
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-drawer-overlay]');
    this.closeBtn = document.querySelector('[data-cart-drawer-close]');
    this.cartIcon = document.querySelector('[data-cart-icon]');
    this.cartItems = document.querySelector('[data-cart-items]');
    this.cartEmpty = document.querySelector('[data-cart-empty]');
    this.cartFooter = document.querySelector('[data-cart-footer]');
    this.cartSubtotal = document.querySelector('[data-cart-subtotal]');
    this.cartItemCount = document.querySelector('[data-cart-item-count]');
    this.cartShippingBar = document.querySelector('[data-cart-shipping-bar]');
    this.cartRecommendations = document.querySelector('[data-cart-recommendations]');
    this.checkoutBtn = document.querySelector('[data-cart-checkout]');
    
    // Debug: Log element selection
    console.log('Cart drawer elements found:', {
      drawer: !!this.drawer,
      cartItems: !!this.cartItems,
      cartEmpty: !!this.cartEmpty,
      cartFooter: !!this.cartFooter
    });
    
    this.isOpen = false;
    this.cartData = null;
    this.isUpdate = false;
    this.cartItemClickHandler = null;
    this.checkoutBtnClickHandler = null;
    this.isProcessing = false;
    
    this.init();
  }

  init() {
    if (!this.drawer) {
      console.warn('Cart drawer not found');
      return;
    }

    // Initialize checkout button if it exists
    if (this.checkoutBtn) {
      this.bindCheckoutButton();
    }

    this.bindEvents();
    this.dispatchEvent('cartDrawer:init');
  }

  bindEvents() {
    // Open drawer when cart icon is clicked
    if (this.cartIcon) {
      this.cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.dispatchEvent('cartDrawer:open');
      });
    }

    // Close drawer events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.dispatchEvent('cartDrawer:close');
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.dispatchEvent('cartDrawer:close');
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.dispatchEvent('cartDrawer:close');
      }
    });

    // Listen for cart updates
    document.addEventListener('cartItemAdded', () => {
      this.isUpdate = true; // Mark as update so items get re-rendered
      this.dispatchEvent('cartDrawer:open');
      // Wait for drawer to open before refreshing
      setTimeout(() => {
        this.dispatchEvent('cartDrawer:refresh');
      }, 100);
    });

    document.addEventListener('cartUpdated', () => {
      this.dispatchEvent('cartDrawer:refresh');
    });

    // Listen for custom cart drawer events
    document.addEventListener('cartDrawer:open', () => this.handleOpen());
    document.addEventListener('cartDrawer:close', () => this.handleClose());
    document.addEventListener('cartDrawer:toggle', () => this.handleToggle());
    document.addEventListener('cartDrawer:refresh', () => this.handleRefresh());
    document.addEventListener('cartDrawer:updateItem', (e) => this.handleUpdateItem(e.detail));
    document.addEventListener('cartDrawer:removeItem', (e) => this.handleRemoveItem(e.detail));

    // Prevent body scroll when drawer is open
    this.drawer.addEventListener('transitionend', () => {
      if (this.isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }

  // Event dispatcher
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail });
    document.dispatchEvent(event);
  }

  // Event handlers
  async handleOpen() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    this.drawer.classList.add('cart-drawer--open');
    document.body.classList.add('cart-drawer-open');
    
    // Load fresh cart data when opening
    await this.loadCartData();
    this.dispatchEvent('cartDrawer:opened');
  }

  handleClose() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.drawer.classList.remove('cart-drawer--open');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';
    
    this.dispatchEvent('cartDrawer:closed');
  }

  handleToggle() {
    if (this.isOpen) {
      this.dispatchEvent('cartDrawer:close');
    } else {
      this.dispatchEvent('cartDrawer:open');
    }
  }

  async handleRefresh() {
    await this.loadCartData();
    this.dispatchEvent('cartDrawer:refreshed');
  }

  async handleUpdateItem(detail) {
    const { itemKey, change } = detail;
    await this.updateItemQuantity(itemKey, change);
  }

  async handleRemoveItem(detail) {
    const { itemKey } = detail;
    await this.removeItem(itemKey);
  }

  async loadCartData() {
    try {
      this.dispatchEvent('cartDrawer:loading');
      
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load cart: ${response.status} ${response.statusText}`);
      }
      
      this.cartData = await response.json();
      this.renderCart();
      
      this.dispatchEvent('cartDrawer:dataLoaded', { cartData: this.cartData });
    } catch (error) {
      console.error('Error loading cart data:', error);
      this.dispatchEvent('cartDrawer:error', { error: error.message });
      
      // Show error state
      if (this.cartItems) {
        this.cartItems.innerHTML = `
          <div class="cart-drawer__error">
            <p>Error loading cart. Please try again.</p>
            <button onclick="location.reload()">Reload Page</button>
          </div>
        `;
      }
    }
  }

  async renderCart() {
    if (!this.cartData) return;

    const { items, item_count, total_price } = this.cartData;

    // Show/hide empty state
    if (items.length === 0) {
      if (this.cartEmpty) this.cartEmpty.style.display = 'block';
      if (this.cartFooter) this.cartFooter.style.display = 'none';
      
      // Clear all cart items when empty
      if (this.cartItems) {
        this.cartItems.innerHTML = '';
      }
      
      this.dispatchEvent('cartDrawer:empty');
      return;
    }

    if (this.cartEmpty) this.cartEmpty.style.display = 'none';
    if (this.cartFooter) this.cartFooter.style.display = 'flex';

    // Always reload from server if this is an update (item was added/removed)
    if (this.isUpdate) {
      // Reload entire cart drawer from server for updates
      await this.reloadCartDrawer();
      // Reset the flag after reloading
      this.isUpdate = false;
    } else {
      // Just bind events for initial load
      this.bindCartItemEvents();
      
      // Update shipping bar and checkout button for initial load
      this.updateShippingBar(total_price);

      if (this.checkoutBtn) {
        // Ensure checkout button has correct href
        const checkoutUrl = this.checkoutBtn.getAttribute('href') || '/checkout';
        this.checkoutBtn.href = checkoutUrl;
        // Bind checkout button click handler
        this.bindCheckoutButton();
      }
    }

    this.dispatchEvent('cartDrawer:rendered', { 
      itemCount: item_count, 
      totalPrice: total_price 
    });
  }


  async updateCartDisplay() {
    try {
      // Get fresh cart data from Shopify
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart data');
      
      const cartData = await response.json();
      
      // Update individual elements without hardcoding HTML
      this.updateCartItems(cartData.items);
      this.updateCartTotals(cartData);
      this.updateShippingBar(cartData.total_price);
      
    } catch (error) {
      console.error('Error updating cart display:', error);
    }
  }


  async reloadCartDrawer() {
    try {
      // Get fresh HTML from server
      const response = await fetch('/?section_id=cart-drawer');
      if (!response.ok) throw new Error('Failed to fetch cart drawer HTML');
      
      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      
      const newCartDrawer = html.querySelector('.cart-drawer');
      if (newCartDrawer && this.drawer) {
        // Replace the content
        this.drawer.innerHTML = newCartDrawer.innerHTML;
        
        // Re-select elements after HTML update
        this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
        this.cartItems = this.drawer.querySelector('[data-cart-items]');
        this.cartEmpty = this.drawer.querySelector('[data-cart-empty]');
        this.cartFooter = this.drawer.querySelector('[data-cart-footer]');
        this.cartSubtotal = this.drawer.querySelector('[data-cart-subtotal]');
        this.cartItemCount = this.drawer.querySelector('[data-cart-item-count]');
        this.cartShippingBar = this.drawer.querySelector('[data-cart-shipping-bar]');
        this.checkoutBtn = this.drawer.querySelector('[data-cart-checkout]');
        
        // Ensure checkout button has correct href after reload
        if (this.checkoutBtn) {
          // Use Shopify's routes.checkout_url or fallback to /checkout
          const checkoutUrl = this.checkoutBtn.getAttribute('href') || '/checkout';
          this.checkoutBtn.href = checkoutUrl;
          // Bind checkout button click handler
          this.bindCheckoutButton();
        }
        
        // Re-bind drawer-specific events
        this.bindDrawerEvents();
        
        this.dispatchEvent('cartDrawer:reloaded');
      }
    } catch (error) {
      console.error('Error reloading cart drawer:', error);
      this.dispatchEvent('cartDrawer:reloadError', { error: error.message });
    }
  }


  bindDrawerEvents() {
    // Close drawer events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.dispatchEvent('cartDrawer:close');
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.dispatchEvent('cartDrawer:close');
      });
    }

    // Bind checkout button
    this.bindCheckoutButton();

    // Bind cart item events
    this.bindCartItemEvents();
  }

  bindCheckoutButton() {
    if (!this.checkoutBtn) return;

    // Remove existing listener if any
    if (this.checkoutBtnClickHandler) {
      this.checkoutBtn.removeEventListener('click', this.checkoutBtnClickHandler);
    }

    // Create new click handler
    this.checkoutBtnClickHandler = (e) => {
      // Prevent default navigation if cart is empty
      if (!this.cartData || !this.cartData.items || this.cartData.items.length === 0) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Ensure href is set correctly before navigation
      let checkoutUrl = this.checkoutBtn.getAttribute('href');
      
      // If href is not set or invalid, use fallback
      if (!checkoutUrl || checkoutUrl === '#' || checkoutUrl === '' || checkoutUrl === window.location.href) {
        checkoutUrl = '/checkout';
        this.checkoutBtn.href = checkoutUrl;
      }

      // Verify the href is now valid
      if (!this.checkoutBtn.href || this.checkoutBtn.href === '#' || this.checkoutBtn.href === '') {
        e.preventDefault();
        e.stopPropagation();
        // Navigate programmatically as fallback
        window.location.href = checkoutUrl;
        return;
      }

      // Ensure href is absolute URL if needed
      if (this.checkoutBtn.href && !this.checkoutBtn.href.startsWith('http') && !this.checkoutBtn.href.startsWith('/')) {
        this.checkoutBtn.href = '/' + this.checkoutBtn.href;
      }

      // Allow normal navigation - don't prevent default or stop propagation
      // The link will navigate normally
    };

    // Add event listener
    this.checkoutBtn.addEventListener('click', this.checkoutBtnClickHandler);
  }

  bindCartItemEvents() {
    // Remove existing listeners to prevent duplication
    if (this.cartItemClickHandler) {
      this.cartItems.removeEventListener('click', this.cartItemClickHandler);
    }

    // Create a single event handler for all cart item interactions
    this.cartItemClickHandler = (e) => {
      // Prevent multiple simultaneous requests
      if (this.isProcessing) {
        e.preventDefault();
        return;
      }

      const decreaseBtn = e.target.closest('[data-cart-item-decrease]');
      const increaseBtn = e.target.closest('[data-cart-item-increase]');
      const removeBtn = e.target.closest('[data-cart-item-remove]');

      if (decreaseBtn) {
        e.preventDefault();
        e.stopPropagation();
        const itemKey = decreaseBtn.dataset.cartItemDecrease;
        this.dispatchEvent('cartDrawer:updateItem', { itemKey, change: -1 });
      } else if (increaseBtn) {
        e.preventDefault();
        e.stopPropagation();
        const itemKey = increaseBtn.dataset.cartItemIncrease;
        this.dispatchEvent('cartDrawer:updateItem', { itemKey, change: 1 });
      } else if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const itemKey = removeBtn.dataset.cartItemRemove;
        this.dispatchEvent('cartDrawer:removeItem', { itemKey });
      }
    };

    // Add the single event listener
    this.cartItems.addEventListener('click', this.cartItemClickHandler);
  }

  async updateItemQuantity(itemKey, change) {
    // Prevent multiple simultaneous requests
    if (this.isProcessing) {
      console.log('Already processing a request, ignoring...');
      return;
    }

    const item = this.cartData.items.find(item => item.key === itemKey);
    if (!item) return;

    const newQuantity = item.quantity + change;
    if (newQuantity <= 0) {
      this.dispatchEvent('cartDrawer:removeItem', { itemKey });
      return;
    }

    this.isProcessing = true;

    try {
      this.dispatchEvent('cartDrawer:updatingItem', { itemKey, newQuantity });
      
      // Use /cart/update.js for better reliability
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: { [itemKey]: newQuantity }
        })
      });

      if (response.ok) {
        const cart = await response.json();
        // Reload cart drawer HTML from server
        await this.reloadCartDrawer();
        this.dispatchEvent('cartDrawer:itemUpdated', { itemKey, newQuantity });
        this.triggerCartUpdated();
      } else {
        if (response.status === 422) {
          // Stock insuficiente
          this.showToast('Stock insuficiente', 'error');
          this.dispatchEvent('cartDrawer:stockError', { itemKey, error: 'Stock insuficiente' });
        } else {
          this.showToast('Error al actualizar el producto', 'error');
          this.dispatchEvent('cartDrawer:updateError', { itemKey, error: 'Failed to update item' });
        }
      }
    } catch (error) {
      console.error('Error updating item quantity:', error);
      this.dispatchEvent('cartDrawer:updateError', { itemKey, error: error.message });
    } finally {
      this.isProcessing = false;
    }
  }

  async removeItem(itemKey) {
    // Prevent multiple simultaneous requests
    if (this.isProcessing) {
      console.log('Already processing a request, ignoring...');
      return;
    }

    this.isProcessing = true;

    try {
      this.dispatchEvent('cartDrawer:removingItem', { itemKey });
      
      // Use /cart/update.js for better reliability
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: { [itemKey]: 0 }
        })
      });

      if (response.ok) {
        const cart = await response.json();
        // Reload cart drawer HTML from server
        await this.reloadCartDrawer();
        this.dispatchEvent('cartDrawer:itemRemoved', { itemKey });
        this.triggerCartUpdated();
      } else {
        this.dispatchEvent('cartDrawer:removeError', { itemKey, error: 'Failed to remove item' });
      }
    } catch (error) {
      console.error('Error removing item:', error);
      this.dispatchEvent('cartDrawer:removeError', { itemKey, error: error.message });
    } finally {
      this.isProcessing = false;
    }
  }

  updateShippingBar(totalPrice) {
    if (!this.cartShippingBar) return;

    const freeShippingThreshold = 5000; // $50.00 in cents
    const remaining = freeShippingThreshold - totalPrice;
    let success = document.querySelector('.cart-drawer__shipping-success');

    if (remaining > 0) {
      success.classList.add('hidden');
      this.cartShippingBar.querySelector('.cart-drawer__shipping-text').classList.remove('hidden');
      let percentage = (totalPrice / freeShippingThreshold) * 100;
      this.cartShippingBar.querySelector('.cart-drawer__shipping-fill').style.width = `${percentage}%`;
      let span = document.getElementById('cart-drawer__shipping-text-remaining');
      span.textContent = this.formatMoney(remaining);

      this.dispatchEvent('cartDrawer:shippingProgress', { 
        totalPrice, 
        remaining, 
        threshold: freeShippingThreshold,
        percentage: (totalPrice / freeShippingThreshold) * 100
      });

    } else {
      success.classList.remove('hidden');
      this.cartShippingBar.querySelector('.cart-drawer__shipping-text').classList.add('hidden');
      this.dispatchEvent('cartDrawer:freeShippingQualified', { totalPrice });
    }
    if (totalPrice == 0) {
      success.classList.add('hidden');
      this.cartShippingBar.classList.add('hidden');
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  triggerCartUpdated() {
    this.dispatchEvent('cartUpdated', { cartData: this.cartData });
  }

  // Public API methods (for external use)
  open() {
    this.dispatchEvent('cartDrawer:open');
  }

  close() {
    this.dispatchEvent('cartDrawer:close');
  }

  toggle() {
    this.dispatchEvent('cartDrawer:toggle');
  }

  refresh() {
    this.dispatchEvent('cartDrawer:refresh');
  }

  isDrawerOpen() {
    return this.isOpen;
  }

  getCartData() {
    return this.cartData;
  }

  showToast(message, type = 'info') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `cart-toast cart-toast--${type}`;
    toast.innerHTML = `
      <div class="cart-toast__content">
        <span class="cart-toast__message">${message}</span>
        <button class="cart-toast__close" aria-label="Cerrar">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    // Add to body
    document.body.appendChild(toast);

    // Auto remove after 4 seconds
    const autoRemove = setTimeout(() => {
      this.removeToast(toast);
    }, 4000);

    // Manual close
    const closeBtn = toast.querySelector('.cart-toast__close');
    closeBtn.addEventListener('click', () => {
      clearTimeout(autoRemove);
      this.removeToast(toast);
    });

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('cart-toast--show');
    });
  }

  removeToast(toast) {
    toast.classList.add('cart-toast--hide');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }


}

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});

// Global Cart Drawer Event System
// This allows external components to interact with the cart drawer through events

/**
 * Cart Drawer Events Documentation
 * 
 * EVENTS YOU CAN LISTEN TO:
 * 
 * cartDrawer:init - Drawer initialized
 * cartDrawer:opened - Drawer opened
 * cartDrawer:closed - Drawer closed
 * cartDrawer:refreshed - Cart data refreshed
 * cartDrawer:dataLoaded - Cart data loaded from server
 * cartDrawer:rendered - Cart rendered with items
 * cartDrawer:empty - Cart is empty
 * cartDrawer:itemsRendered - Cart items rendered
 * cartDrawer:itemUpdated - Item quantity updated
 * cartDrawer:itemRemoved - Item removed from cart
 * cartDrawer:updatingItem - Item update in progress
 * cartDrawer:removingItem - Item removal in progress
 * cartDrawer:updateError - Error updating item
 * cartDrawer:removeError - Error removing item
 * cartDrawer:shippingProgress - Shipping progress updated
 * cartDrawer:freeShippingQualified - Free shipping qualified
 * cartDrawer:error - General error occurred
 * 
 * EVENTS YOU CAN DISPATCH:
 * 
 * cartDrawer:open - Open the drawer
 * cartDrawer:close - Close the drawer
 * cartDrawer:toggle - Toggle drawer open/close
 * cartDrawer:refresh - Refresh cart data
 * cartDrawer:updateItem - Update item quantity (detail: {itemKey, change})
 * cartDrawer:removeItem - Remove item (detail: {itemKey})
 * 
 * EXAMPLE USAGE:
 * 
 * // Listen to drawer events
 * document.addEventListener('cartDrawer:opened', () => {
 *   console.log('Cart drawer opened!');
 * });
 * 
 * // Dispatch drawer events
 * document.dispatchEvent(new CustomEvent('cartDrawer:open'));
 * document.dispatchEvent(new CustomEvent('cartDrawer:updateItem', {
 *   detail: { itemKey: '123', change: 1 }
 * }));
 */

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartDrawer;
}
